import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  listClassSections,
  getRoster,
  createClassSectionsBulk,
  listSchoolPeriods,
} from '../../../api/schoolConfigApi';
import { ApiError } from '../../../api/http';
import { showSuccess, showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import { parseCsv, escapeCsv, readFileAsText } from '../../../utils/csvParsing';
import CollapsibleSection from '../../common/CollapsibleSection';
import SearchFilter from '../../common/SearchFilter';
import CsvFormatExplainer from '../CsvFormatExplainer';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { useUserNameResolver } from '../../../hooks/useUserNameResolver';
import adminStyles from '../AdminPanel.module.css';
import styles from './ClassSectionsPanel.module.css';
import type { components } from '../../../api/types.gen';

type ClassSectionResponse = components['schemas']['ClassSectionResponse'];
type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];
type BulkClassSectionSyncBody = components['schemas']['BulkClassSectionSyncBody'];

/** studentId -> {schoolPeriodId: teacherEmail} */
type RosterAssignments = Record<number, Record<number, string>>;

/** One rendered roster-table row (studentId -> period -> teacher email). */
interface RosterRow {
  studentId: number;
  name: string;
  email: string;
  assignments: Record<number, string>;
}

/** Params for buildBulkPayload — a scoped full-replace over touched periods. */
interface BuildBulkPayloadParams {
  overrideStudentId?: number;
  overrideAssignments?: Record<number, string>;
  excludeStudentId?: number;
  touchedPeriodIds: number[];
}

/** One parsed CSV student row, before it's reshaped into the request body. */
interface ParsedBulkCsvStudent {
  rowNum: number;
  email: string;
  assignments: {
    schoolPeriodId: number;
    periodName: string;
    teacherEmail: string;
  }[];
}

/** Result of parseBulkCsv — structural validation of the pasted CSV. */
interface ParseBulkCsvResult {
  errors: string[];
  students: ParsedBulkCsvStudent[];
  periodIds: number[];
}

interface ClassSectionsPanelProps {
  schoolId?: number;
}

class RosterSyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RosterSyncError';
  }
}

// New panel (NOT a port — no legacy Firebase counterpart) built as part of
// the Firebase -> hallpass-backend migration (Admin step 7). Exposes
// hallpass-backend's class-sections API
// (apps/schools-api/src/routes/class-section.ts), which the legacy
// AdminPanel.js never had a UI for, and becomes the new home for
// per-class-section pass limits (ClassSection.maxOutAtOnce) — the confirmed
// replacement for the legacy perClassLimit system-setting dropped from the
// pass-policy panel in an earlier migration step.
//
// Real-time push is intentionally skipped in favor of a plain
// fetch-on-mount + refetch-after-mutation, same as this migration's other
// admin config panels (DestinationsPanel.js, StudentPassLimitsPanel.js).
//
// listClassSections only returns {id, schoolId, schoolPeriodId, periodName,
// teacherId, name, maxOutAtOnce} — no denormalized teacher name/email — so
// teacher display
// names are resolved separately via adminUsersApi.listUsers({ids}), the same
// pattern StudentPassLimitsPanel.js uses to resolve student names for
// pass-limit overrides.

// Builds the sample bulk-CSV content (email,<period1>,<period2>,...) from
// the school's actual configured periods. Shared by the on-page <pre>
// preview and the "Download Sample CSV" button so they can't drift apart.
// Two sample rows illustrate both a shared teacher (grouped into one
// section server-side) and blank = not-enrolled.
function buildSampleBulkCsv(schoolPeriods: SchoolPeriodResponse[]): string {
  const header = [
    'email',
    ...schoolPeriods.map((sp) => escapeCsv(sp.name)),
  ].join(',');
  const sampleRows: { email: string; teacherEmailsByColumn: string[] }[] = [
    { email: 'student1@school.edu', teacherEmailsByColumn: ['teacher1@school.edu', 'teacher2@school.edu'] },
    { email: 'student2@school.edu', teacherEmailsByColumn: ['', 'teacher1@school.edu'] },
  ];
  const rows = sampleRows.map(({ email, teacherEmailsByColumn }) => {
    const cells = schoolPeriods.map((_, i) =>
      escapeCsv(teacherEmailsByColumn[i] || '')
    );
    return [escapeCsv(email), ...cells].join(',');
  });
  return [header, ...rows].join('\n');
}

const ClassSectionsPanel = ({
  schoolId: schoolIdProp,
}: ClassSectionsPanelProps = {}) => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;

  const [loadingStates, setLoading] = useLoadingStates({
    loadSections: false,
    loadSchoolPeriods: false,
    loadRoster: false,
    saveRosterEdit: false,
    deleteRosterRow: false,
    bulkCreate: false,
  });

  // Section list — plain fetch-on-mount + refetch-after-mutation. Not
  // rendered directly (no more per-section cards); it's the source of truth
  // the roster table is built from (one getRoster call per section) and the
  // sync target every roster-table edit/delete writes back through via
  // createClassSectionsBulk.
  const [sections, setSections] = useState<ClassSectionResponse[]>([]);

  // The school's canonical period registry (SchoolPeriod rows) — see
  // docs/SCHEMA_PLAN.md. Powers the roster table's per-period columns and
  // the bulk CSV's period-column validation (parseBulkCsv below).
  const [schoolPeriods, setSchoolPeriods] = useState<SchoolPeriodResponse[]>([]);

  // Teacher id -> {id, name, email}, resolved lazily as sections referencing
  // them are seen.
  const [teacherNames, resolveTeacherNames] = useUserNameResolver(
    'resolveTeacherNamesForClassSections'
  );

  // Guards against a stale response clobbering `sections` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchSections = useCallback(
    async (isCancelled: () => boolean = () => false): Promise<void> => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading('loadSections', true);
      try {
        const result = await listClassSections(schoolId);
        if (!isCancelled()) {
          setSections(result.data);
          await resolveTeacherNames(result.data.map((s) => s.teacherId));
        }
      } catch (error) {
        logError(error, 'loadClassSections');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load class sections. Please try again.'
            )
          );
          setSections([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadSections', false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId]
  );

  // Same guard as fetchSections above, for `schoolPeriods`.
  const fetchSchoolPeriods = useCallback(
    async (isCancelled: () => boolean = () => false): Promise<void> => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading('loadSchoolPeriods', true);
      try {
        const result = await listSchoolPeriods(schoolId);
        if (!isCancelled()) setSchoolPeriods(result.data);
      } catch (error) {
        logError(error, 'loadSchoolPeriodsForClassSections');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load periods. Please try again.'
            )
          );
          setSchoolPeriods([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadSchoolPeriods', false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchSections(() => cancelled);
    fetchSchoolPeriods(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchSections, fetchSchoolPeriods]);

  // Roster table — one row per student, one column per school period,
  // mirroring the bulk-CSV wide format (email,<period1>,<period2>,...).
  // There's no single "list all enrollments" endpoint, so this is built by
  // fetching every section's roster in parallel and grouping by student.
  const [rosterAssignments, setRosterAssignments] = useState<RosterAssignments>(
    {}
  ); // studentId -> {schoolPeriodId: teacherEmail}
  const [rosterStudentIds, setRosterStudentIds] = useState<number[]>([]);
  const [rosterFilter, setRosterFilter] = useState('');
  const [studentNames, resolveStudentNames] = useUserNameResolver(
    'resolveStudentNamesForClassSectionsRoster'
  );

  // Guards against a stale response clobbering the roster: fetchRoster is
  // re-triggered not just by schoolId changes but by ordinary `sections`/
  // `teacherNames` updates too (e.g. teacherNames resolving asynchronously
  // right after fetchSections sets `sections`), so overlapping runs happen
  // in normal use, not just a schoolId-switch edge case. If a newer run
  // starts before an older one's getRoster calls resolve, the effect below
  // flips `cancelled` on cleanup so the older run's late response is
  // dropped instead of overwriting state with stale (e.g.
  // not-yet-teacherNames-resolved) data. Mirrors the cancelled-flag pattern
  // in useAdminSetupChecklist.js's fetchCounts.
  const fetchRoster = useCallback(
    async (isCancelled: () => boolean = () => false): Promise<void> => {
      if (!schoolId || sections.length === 0) {
        if (!isCancelled()) {
          setRosterAssignments({});
          setRosterStudentIds([]);
        }
        return;
      }
      if (!isCancelled()) setLoading('loadRoster', true);
      try {
        const rosters = await Promise.all(
          sections.map((section) =>
            getRoster(schoolId, section.id).then((result) => ({
              section,
              studentIds: result.studentIds,
            }))
          )
        );

        const assignments: RosterAssignments = {};
        rosters.forEach(({ section, studentIds }) => {
          const teacherEmail = teacherNames[section.teacherId]?.email || '';
          studentIds.forEach((studentId) => {
            if (!assignments[studentId]) assignments[studentId] = {};
            assignments[studentId][section.schoolPeriodId] = teacherEmail;
          });
        });

        const ids = Object.keys(assignments).map(Number);
        await resolveStudentNames(ids);
        if (!isCancelled()) {
          setRosterAssignments(assignments);
          setRosterStudentIds(ids);
        }
      } catch (error) {
        logError(error, 'loadClassSectionsRoster');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load class roster. Please try again.'
            )
          );
          setRosterAssignments({});
          setRosterStudentIds([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadRoster', false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId, sections, teacherNames, resolveStudentNames]
  );

  useEffect(() => {
    let cancelled = false;
    fetchRoster(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchRoster]);

  const rosterRows: RosterRow[] = rosterStudentIds
    .map((studentId) => {
      const student = studentNames[studentId];
      return {
        studentId,
        name: student?.name || `Student #${studentId}`,
        email: student?.email || '',
        assignments: rosterAssignments[studentId] || {},
      };
    })
    .filter((row) => {
      if (!rosterFilter.trim()) return true;
      const q = rosterFilter.trim().toLowerCase();
      return (
        row.name.toLowerCase().includes(q) ||
        row.email.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Inline per-row edit — which (period -> teacher email) assignments this
  // one student has. Saving and deleting both resubmit only the periods
  // touched by that row via createClassSectionsBulk (schoolPeriodIds =
  // union of the row's previous and new period assignments, so the
  // sweep-delete is scoped to just those periods) with just this row's
  // assignments added/changed/omitted — the same "full replace per
  // referenced period" contract the CSV paste flow above already relies
  // on, just scoped to the touched periods instead of every period.
  const [editingStudentId, setEditingStudentId] = useState<number | null>(null);
  const [editAssignments, setEditAssignments] = useState<Record<number, string>>(
    {}
  );

  const startEditStudent = (row: RosterRow): void => {
    setEditingStudentId(row.studentId);
    setEditAssignments({ ...row.assignments });
  };

  const cancelEditStudent = (): void => {
    setEditingStudentId(null);
    setEditAssignments({});
  };

  const buildBulkPayload = ({
    overrideStudentId,
    overrideAssignments,
    excludeStudentId,
    touchedPeriodIds,
  }: BuildBulkPayloadParams): BulkClassSectionSyncBody => {
    const periodIdSet = new Set(touchedPeriodIds);

    const studentsWithAssignments = rosterStudentIds
      .filter((id) => id !== excludeStudentId)
      .map((id) => {
        const assignments =
          id === overrideStudentId
            ? overrideAssignments ?? {}
            : rosterAssignments[id] || {};
        return {
          id,
          assignments: Object.entries(assignments)
            // Trim before the blank check so a whitespace-only cell (e.g. a
            // stray space typed into a roster edit input) is treated as "not
            // enrolled" instead of being POSTed as a bogus teacher email.
            .map(([schoolPeriodId, teacherEmail]) => [
              schoolPeriodId,
              (teacherEmail || '').trim(),
            ])
            .filter(
              ([schoolPeriodId, teacherEmail]) =>
                teacherEmail && periodIdSet.has(Number(schoolPeriodId))
            )
            .map(([schoolPeriodId, teacherEmail]) => ({
              schoolPeriodId: Number(schoolPeriodId),
              teacherEmail,
            })),
        };
      })
      .filter((s) => s.assignments.length > 0);

    // A student only ends up in the outgoing payload if they have an
    // assignment in one of the touched periods (see finding-2's scoping
    // change below). Don't let a student with an unresolved name/email
    // (useUserNameResolver silently leaves ids unresolved on a transient
    // listUsers failure -- see useUserNameResolver.js) be silently coerced
    // out of the sync: that would permanently unenroll them from every
    // period in this payload with no error and no warning.
    const unresolved = studentsWithAssignments.filter((s) => !studentNames[s.id]?.email);
    if (unresolved.length > 0) {
      throw new RosterSyncError(
        `Cannot save — ${unresolved.length} student${unresolved.length === 1 ? '' : 's'} affected by this change ${unresolved.length === 1 ? 'has' : 'have'} an unresolved name/email and would be silently dropped from their period(s). Reload the page and try again.`
      );
    }

    // Mirror-image of the check above, for teachers: fetchRoster defaults
    // an unresolved section teacher's email to '' (same
    // useUserNameResolver transient-failure case), and that '' is
    // indistinguishable from "not enrolled" once it lands in
    // rosterAssignments. Checked against the ORIGINAL rosterAssignments —
    // including the edited row (overrideStudentId). The edited row is not
    // exempt: a blank in its *originally-loaded* assignments means the
    // section's teacher failed to resolve, which is unambiguous (this reads
    // rosterAssignments, not the edit inputs, so a teacher email the user
    // deliberately cleared in editAssignments still saves fine). Saving such
    // a row would silently unenroll the student from that period, so block
    // it and make the user reload once teachers resolve.
    const unresolvedTeacherStudentIds = rosterStudentIds
      .filter((id) => id !== excludeStudentId)
      .filter((id) =>
        Object.entries(rosterAssignments[id] || {}).some(
          ([schoolPeriodId, teacherEmail]) =>
            !teacherEmail && periodIdSet.has(Number(schoolPeriodId))
        )
      );
    if (unresolvedTeacherStudentIds.length > 0) {
      throw new RosterSyncError(
        `Cannot save — ${unresolvedTeacherStudentIds.length} student${unresolvedTeacherStudentIds.length === 1 ? '' : 's'} in a touched period ${unresolvedTeacherStudentIds.length === 1 ? 'is' : 'are'} enrolled with a teacher whose name/email failed to resolve and would be silently unenrolled. Reload the page and try again.`
      );
    }

    return {
      schoolPeriodIds: [...periodIdSet],
      students: studentsWithAssignments.map((s) => ({
        email: studentNames[s.id].email,
        assignments: s.assignments,
      })),
    };
  };

  const saveStudentEdits = async (): Promise<void> => {
    if (!schoolId || editingStudentId === null) return;
    setLoading('saveRosterEdit', true);
    try {
      const previousAssignments = rosterAssignments[editingStudentId] || {};
      const touchedPeriodIds = [
        ...new Set(
          [...Object.keys(previousAssignments), ...Object.keys(editAssignments)].map(Number)
        ),
      ];
      const body = buildBulkPayload({
        overrideStudentId: editingStudentId,
        overrideAssignments: editAssignments,
        touchedPeriodIds,
      });
      await createClassSectionsBulk(schoolId, body);
      showSuccess('Roster updated.');
      cancelEditStudent();
      await fetchSections();
    } catch (error) {
      logError(error, 'updateClassSectionsRosterRow');
      if (error instanceof RosterSyncError) {
        showError(error.message);
      } else if (error instanceof ApiError && error.status === 422) {
        showError(
          getContextualError(
            error,
            undefined,
            'Could not save — check the teacher emails entered.'
          )
        );
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to update roster. Please try again.'
          )
        );
      }
    } finally {
      setLoading('saveRosterEdit', false);
    }
  };

  const deleteStudentFromRoster = async (row: RosterRow): Promise<void> => {
    if (!schoolId) return;
    if (
      !window.confirm(`Remove ${row.name} from all class sections?`)
    ) {
      return;
    }

    setLoading('deleteRosterRow', true);
    try {
      const touchedPeriodIds = Object.keys(row.assignments).map(Number);
      const body = buildBulkPayload({ excludeStudentId: row.studentId, touchedPeriodIds });
      await createClassSectionsBulk(schoolId, body);
      showSuccess(`Removed ${row.name} from all sections.`);
      await fetchSections();
    } catch (error) {
      logError(error, 'removeStudentFromClassSections');
      if (error instanceof RosterSyncError) {
        showError(error.message);
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to remove student. Please try again.'
          )
        );
      }
    } finally {
      setLoading('deleteRosterRow', false);
    }
  };

  // Bulk sync — pasted wide-format CSV (email,<period1>,<period2>,...; each
  // cell is the enrolling teacher's email, blank = not enrolled), mirroring
  // UserManagementPanel.js's CSV convention (shared parseCsv tokenizer,
  // RFC 4180 quoting supported). Weekly-safe: re-uploading is a full
  // replace of enrollments for every period referenced in the CSV, not an
  // additive import.
  //
  // Unlike the old create-only endpoint, email->id resolution and
  // section/enrollment diffing now happen entirely server-side
  // (createClassSectionsBulk sends the raw parsed rows) — the client's job
  // is limited to structural CSV validation (header shape, period-column
  // recognition, row shape) so obvious mistakes are caught instantly,
  // before the all-or-nothing server round trip.
  const [bulkCsvText, setBulkCsvText] = useState('');

  // Synchronous, no network calls — uses the already-loaded `schoolPeriods`
  // state so period-column errors surface instantly. Returns
  // {errors, students, periodIds} where students is
  // [{rowNum, email, assignments: [{schoolPeriodId, periodName, teacherEmail}]}]
  // and periodIds is the full list of period ids represented by the CSV's
  // columns; periodName is kept for error messages and dropped when
  // building the request body in submitBulkCreate.
  const parseBulkCsv = (
    csvText: string,
    periods: SchoolPeriodResponse[]
  ): ParseBulkCsvResult => {
    const { headers, dataRows } = parseCsv(csvText);

    if (dataRows.length === 0) {
      return {
        errors: ['File must contain a header row and at least one data row.'],
        students: [],
        periodIds: [],
      };
    }

    if (headers[0] !== 'email') {
      return {
        errors: [
          "The first column must be 'email'. Header should be: email,<period1>,<period2>,...",
        ],
        students: [],
        periodIds: [],
      };
    }

    if (headers.length < 2) {
      return {
        errors: ['At least one period column is required after email.'],
        students: [],
        periodIds: [],
      };
    }

    // parseCsv lowercases the header row (its documented contract), which
    // loses the casing we want to echo back in "unrecognized column"
    // errors — re-derive the original-case header cells from the raw text.
    // Falls back to the lowercased headers if a quoted comma in a header
    // throws off this naive split (only used for error-message display,
    // never for matching).
    const rawHeaderCells = csvText
      .split(/\r\n|\n/)[0]
      .split(',')
      .map((h) => h.trim().replace(/^"|"$/g, ''));
    const originalHeaders =
      rawHeaderCells.length === headers.length ? rawHeaderCells : headers;

    // Match each period column against the school's canonical period
    // names, case-insensitively — headers are already lowercased above, so
    // this is a lowercase-to-lowercase comparison; case-sensitive matching
    // isn't recoverable here.
    const periodByLowerName = new Map<string, SchoolPeriodResponse>();
    const collidedLowerNames = new Set<string>();
    periods.forEach((sp) => {
      const lower = sp.name.toLowerCase();
      if (periodByLowerName.has(lower)) {
        collidedLowerNames.add(lower);
      } else {
        periodByLowerName.set(lower, sp);
      }
    });

    const unrecognized: string[] = [];
    const duplicates: string[] = [];
    const ambiguous: string[] = [];
    const seenLower = new Set<string>();
    const columnPeriods = headers.slice(1).map((h, i) => {
      if (collidedLowerNames.has(h)) {
        ambiguous.push(originalHeaders[i + 1]);
        return undefined;
      }
      const period = periodByLowerName.get(h);
      if (!period) {
        unrecognized.push(originalHeaders[i + 1]);
      } else if (seenLower.has(h)) {
        duplicates.push(originalHeaders[i + 1]);
      } else {
        seenLower.add(h);
      }
      return period;
    });

    const errors: string[] = [];
    if (unrecognized.length > 0) {
      errors.push(
        `Unrecognized period column(s): ${unrecognized.join(', ')}.`
      );
    }
    if (ambiguous.length > 0) {
      errors.push(
        `Ambiguous period column(s): ${ambiguous.join(', ')}. Multiple periods at this school share this name in different letter case — rename them so each period name is unique regardless of case, then re-upload.`
      );
    }
    if (duplicates.length > 0) {
      errors.push(`Duplicate period column(s): ${duplicates.join(', ')}.`);
    }
    if (errors.length > 0) {
      return { errors, students: [], periodIds: [] };
    }

    const periodIds = columnPeriods
      .filter((period): period is SchoolPeriodResponse => period !== undefined)
      .map((period) => period.id);

    const students: ParsedBulkCsvStudent[] = [];
    // `${email.toLowerCase()}:${schoolPeriodId}` -> teacherEmail. Defense
    // in depth for the same-student-different-teacher-same-period case —
    // also enforced server-side, but catching it instantly client-side
    // avoids a round trip.
    const teacherByStudentPeriod = new Map<string, string>();

    dataRows.forEach((values, i) => {
      const rowNum = i + 2;

      if (values.length !== headers.length) {
        errors.push(
          `Row ${rowNum}: Expected ${headers.length} columns but found ${values.length}. Check for missing commas or extra commas.`
        );
        return;
      }

      const email = values[0] || '';
      if (!email) {
        errors.push(`Row ${rowNum}: email is required.`);
        return;
      }

      const assignments: ParsedBulkCsvStudent['assignments'] = [];
      columnPeriods.forEach((period, col) => {
        if (!period) return;
        const teacherEmail = values[col + 1] || '';
        if (!teacherEmail) return;

        const key = `${email.toLowerCase()}:${period.id}`;
        const priorTeacher = teacherByStudentPeriod.get(key);
        if (
          priorTeacher !== undefined &&
          priorTeacher.toLowerCase() !== teacherEmail.toLowerCase()
        ) {
          errors.push(
            `Row ${rowNum}: student "${email}" is assigned a different teacher for period "${period.name}" than a previous row.`
          );
          return;
        }
        teacherByStudentPeriod.set(key, teacherEmail);
        assignments.push({
          schoolPeriodId: period.id,
          periodName: period.name,
          teacherEmail,
        });
      });

      students.push({ rowNum, email, assignments });
    });

    return { errors, students, periodIds };
  };

  const submitBulkCreate = async (): Promise<void> => {
    if (!schoolId) return;
    const {
      errors: parseErrors,
      students,
      periodIds,
    } = parseBulkCsv(bulkCsvText, schoolPeriods);
    if (parseErrors.length > 0) {
      showError(`CSV format issues found:\n\n${parseErrors.join('\n')}`);
      return;
    }

    setLoading('bulkCreate', true);
    try {
      const body: BulkClassSectionSyncBody = {
        schoolPeriodIds: periodIds,
        students: students.map((s) => ({
          email: s.email,
          assignments: s.assignments.map((a) => ({
            schoolPeriodId: a.schoolPeriodId,
            teacherEmail: a.teacherEmail,
          })),
        })),
      };
      const result = await createClassSectionsBulk(schoolId, body);
      showSuccess(
        `Synced ${result.periodsSynced} period${result.periodsSynced !== 1 ? 's' : ''}: ` +
          `${result.sectionsCreated} new section${result.sectionsCreated !== 1 ? 's' : ''}, ` +
          `${result.sectionsReused} existing section${result.sectionsReused !== 1 ? 's' : ''} updated, ` +
          `${result.enrollmentsSet} enrollment${result.enrollmentsSet !== 1 ? 's' : ''} set.`
      );
      setBulkCsvText('');
      const fileInput = document.querySelector<HTMLInputElement>(
        '#bulk-class-sections-csv-file'
      );
      if (fileInput) {
        fileInput.value = '';
      }
      await fetchSections();
    } catch (error) {
      logError(error, 'createClassSectionsBulk');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to sync class sections. This is all-or-nothing, so nothing was changed.'
        )
      );
    } finally {
      setLoading('bulkCreate', false);
    }
  };

  const handleBulkCsvFileSelection = async (
    file: File | undefined
  ): Promise<void> => {
    if (!file) return;
    try {
      setBulkCsvText(await readFileAsText(file));
    } catch (error) {
      showError(error instanceof Error ? error.message : String(error));
      const fileInput = document.querySelector<HTMLInputElement>(
        '#bulk-class-sections-csv-file'
      );
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const downloadSampleBulkCsv = (): void => {
    const csvContent = buildSampleBulkCsv(schoolPeriods);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_sections_sample_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <CollapsibleSection
      title="Class Sections"
      icon="🏫"
      id="class-sections"
      defaultExpanded={false}
    >
      <div className="section-content">
        <p className="text-muted" style={{ marginBottom: '20px' }}>
          Manage class sections, their assigned teacher, per-section pass limits
          (the replacement for the old per-class pass limit setting), and
          student rosters.
        </p>

        <div className={styles['csv-upload-section']}>
          <CsvFormatExplainer
            title="Bulk Create Sections + Rosters"
            summary="Upload or paste a wide-format, one-student-per-row sheet to sync sections and rosters."
            format="email,<period1>,<period2>,..."
            columns={[
              {
                name: 'email',
                description: "the student's email address (the first column).",
              },
              {
                name: '<period>',
                description: (
                  <>
                    one column per period — the header must exactly match an
                    existing period name (see the Period Management section
                    above; matching is case-insensitive). Each cell holds the
                    enrolling teacher's email, or is left blank if that student
                    isn't enrolled in that period. Sections are created/reused
                    automatically by grouping students that share a (period,
                    teacher) pair.
                  </>
                ),
              },
            ]}
            note={
              <>
                ⚠️ This is a weekly-safe full sync, not an additive import: for
                every period referenced anywhere in the CSV, re-uploading
                replaces that period's enrollments entirely — students left off
                (or blanked out) are removed, and it's all-or-nothing (nothing
                changes if any row fails).
              </>
            }
            noteTone="warning"
            example={buildSampleBulkCsv(schoolPeriods)}
          />

          <div className={styles['form-group']}>
            <label htmlFor="bulk-class-sections-csv-file">
              Choose CSV File:
            </label>
            <input
              id="bulk-class-sections-csv-file"
              type="file"
              accept=".csv"
              className={adminStyles['file-input']}
              disabled={loadingStates.bulkCreate}
              onChange={(e) => handleBulkCsvFileSelection(e.target.files?.[0])}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="bulk-class-sections-csv">Or Paste CSV Data:</label>
            <textarea
              id="bulk-class-sections-csv"
              className={styles['csv-textarea']}
              rows={3}
              value={bulkCsvText}
              onChange={(e) => setBulkCsvText(e.target.value)}
              placeholder="email,Period 1,Period 2,..."
              disabled={loadingStates.bulkCreate}
            />
          </div>

          {schoolPeriods.length === 0 && (
            <p style={{ color: '#b45309' }}>
              No periods configured yet — create periods above before
              syncing sections.
            </p>
          )}

          <div className={adminStyles['button-group']}>
            <button
              className="primary-button"
              onClick={submitBulkCreate}
              disabled={
                loadingStates.bulkCreate ||
                !bulkCsvText.trim() ||
                schoolPeriods.length === 0
              }
            >
              {loadingStates.bulkCreate ? 'Syncing...' : 'Create Sections'}
            </button>
            <button
              className="action-button"
              onClick={downloadSampleBulkCsv}
              disabled={schoolPeriods.length === 0}
            >
              Download Sample CSV
            </button>
          </div>
        </div>

        <div className="existing-accounts-section">
          <h4>Class Roster ({rosterRows.length})</h4>

          <SearchFilter
            value={rosterFilter}
            onChange={setRosterFilter}
            placeholder="Search by student name or email..."
            disabled={loadingStates.loadRoster}
          />

          {loadingStates.loadRoster ? (
            <div className="loading">Loading roster...</div>
          ) : rosterRows.length === 0 ? (
            <div className="no-results">
              {rosterFilter
                ? 'No students found matching search criteria.'
                : 'No students enrolled yet. Use Bulk Create Sections + Rosters above.'}
            </div>
          ) : (
            <div className={styles['roster-table-container']}>
              <table className={styles['roster-table']}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    {schoolPeriods.map((period) => (
                      <th key={period.id}>{period.name}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rosterRows.map((row) => {
                    const isEditing = editingStudentId === row.studentId;
                    return (
                      <tr key={row.studentId}>
                        <td>{row.name}</td>
                        <td>{row.email}</td>
                        {schoolPeriods.map((period) => (
                          <td key={period.id}>
                            {isEditing ? (
                              <input
                                type="text"
                                className={styles['roster-cell-input']}
                                aria-label={`${row.name} — ${period.name} teacher email`}
                                value={editAssignments[period.id] || ''}
                                onChange={(e) =>
                                  setEditAssignments((prev) => ({
                                    ...prev,
                                    [period.id]: e.target.value,
                                  }))
                                }
                                placeholder="Not enrolled"
                                disabled={loadingStates.saveRosterEdit}
                              />
                            ) : row.assignments[period.id] ? (
                              row.assignments[period.id]
                            ) : (
                              <span className={styles['roster-empty-cell']}>
                                —
                              </span>
                            )}
                          </td>
                        ))}
                        <td>
                          {isEditing ? (
                            <div className={adminStyles['button-group']}>
                              <button
                                onClick={saveStudentEdits}
                                disabled={loadingStates.saveRosterEdit}
                                className={adminStyles['add-btn']}
                              >
                                {loadingStates.saveRosterEdit
                                  ? 'Saving...'
                                  : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditStudent}
                                disabled={loadingStates.saveRosterEdit}
                                className={adminStyles['remove-btn']}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className={adminStyles['button-group']}>
                              <button
                                onClick={() => startEditStudent(row)}
                                disabled={loadingStates.deleteRosterRow}
                                className={adminStyles['btn-edit']}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteStudentFromRoster(row)}
                                disabled={loadingStates.deleteRosterRow}
                                className={adminStyles['remove-btn']}
                              >
                                {loadingStates.deleteRosterRow
                                  ? 'Removing...'
                                  : 'Delete'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default ClassSectionsPanel;
